import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

export interface DeviceInfo {
  os: string;
  browser: string;
  screenResolution: string;
  language: string;
  version: string; //Only used in customer-ui for versionCustomer
  userAgent?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceInfoService {
  platformId = inject(PLATFORM_ID);

  getDeviceInfo(): DeviceInfo | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const ua = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const language = navigator.language;

    const browserInfo = this.detectBrowser(ua);
    const osInfo = this.detectOS(ua);

    return {
      os: osInfo,
      browser: `${browserInfo.name} ${browserInfo.version}`,
      screenResolution: `${screenWidth}x${screenHeight}`,
      language,
      version: '',
      userAgent: ua,
    };
  }

  private detectBrowser(ua: string): { name: string; version: string } {
    // Order matters: check specific browsers before generic ones
    const browsers = [
      // In-app browsers (check first, they also contain Chrome/Safari strings)
      { name: 'Instagram', regex: /Instagram[\s/](\S+)/ },
      { name: 'Facebook', regex: /FBAN.*FBAV\/(\S+)/ },
      // Desktop/mobile browsers
      { name: 'Edge', regex: /Edg\/(\S+)/ },
      { name: 'Opera', regex: /OPR\/(\S+)/ },
      { name: 'Chrome', regex: /Chrome\/(\S+)/ },
      { name: 'Firefox', regex: /Firefox\/(\S+)/ },
      { name: 'Safari', regex: /Version\/(\S+)/ },
      { name: 'IE', regex: /Trident.*rv:(\S+)/ },
    ];

    for (const browser of browsers) {
      const match = ua.match(browser.regex);
      if (match) {
        return { name: browser.name, version: match[1] };
      }
    }

    // Fallback: include truncated UA for debugging unknown browsers
    return { name: 'Unknown', version: ua.slice(0, 100) };
  }

  private detectOS(ua: string): string {
    // Check mobile before desktop (iOS Safari sends "Macintosh" in UA since iOS 13)
    if (/iPhone|iPod/.test(ua)) return 'iOS';
    if (/iPad/.test(ua)) return 'iPadOS';
    if (ua.includes('Android')) return 'Android';
    // Heuristic: Mac UA + touch support → likely iPad with desktop UA (iPadOS 13+)
    if (ua.includes('Macintosh') && navigator.maxTouchPoints > 1) return 'iPadOS';
    if (ua.includes('Win')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    return 'Unknown OS';
  }
}
